import { computed } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { User } from '@interfaces/user/user';
import { GlobalConfig } from '@interfaces/global-config';
import { UserAvailabilityStatus } from '../enums/user-availability-status';
import { HttpStatusCode } from '@angular/common/http';

const authState: {
  user: User;
  isLoading: boolean;
  error?: string;
  statusCode: HttpStatusCode;
} = {
  user: {} as User,
  isLoading: false,
  statusCode: HttpStatusCode.Ok,
};

export const AuthStore = signalStore(
  { providedIn: 'root' },
  withState(authState),
  withComputed((store) => ({
    isAuthenticated: computed(() => !!store?.user()?.id),
    role: computed(() => store?.user()?.role),
  })),
  withMethods(({ user, ...store }) => ({
    login(authUser: User | undefined) {
      if (user()?.password) {
        authUser!.password = user()?.password;
      }

      const popup =
        (authUser as any)?.notificationSettings?.popupNotifications ??
        (authUser as any)?.popupNotifications ??
        [];

      const mergedUser: User = {
        ...(authUser as User),
        notificationSettings: { popupNotifications: popup },
      };

      delete (mergedUser as any).popupNotifications;

      sessionStorage.setItem('user', JSON.stringify({ user: mergedUser }));
      patchState(store, { user: mergedUser });
    },
    setPassword(password: string) {
      const mergedUser = { ...user(), password };
      patchState(store, { user: mergedUser });
    },
    setErrorStatusCode(statusCode: HttpStatusCode) {
      patchState(store, { user: user(), statusCode, isLoading: false });
    },
    setIsLoading(isLoading: boolean) {
      patchState(store, { user: user(), isLoading });
    },
    updateUserGlobalConfig(config: GlobalConfig) {
      const updatedUser = { ...user(), globalConfiguration: config };
      sessionStorage.setItem('user', JSON.stringify({ user: updatedUser }));
      patchState(store, { user: updatedUser });
    },
    updateAvailabilityStatus(status: UserAvailabilityStatus) {
      const updatedUser = { ...user(), availabilityStatus: status };
      sessionStorage.setItem('user', JSON.stringify({ user: updatedUser }));
      patchState(store, { user: updatedUser });
    },
    logOut() {
      sessionStorage.clear();
      localStorage.clear();
      patchState(store, { user: undefined });
    },
  })),
  withHooks({
    onInit({ user, ...store }) {
      const userAsString = sessionStorage.getItem('user');
      if (userAsString) {
        const user = JSON.parse(userAsString);
        patchState(store, user);
      }
    },
    onDestroy({ user, ...store }) {
      store.logOut();
    },
  }),
);
